import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { TextInput } from "components/common/FormControls";
import { Button } from "@material-ui/core";
import { normalizeZipCode } from "common/utilities/ generic";
import appConstant from "common/constants/appConstant";
import moment from "moment";
import zipCodeValidationApi from "services/rest/zipCodeValidationApi";
import { get, isEmpty } from "lodash";
import logger from "common/utilities/logger";
import ButtonLoading from "components/common/LoadingIndicator/buttonLoading";

const FormStep6 = ({ styles, handleFormChange, data, setStepNo, formData }) => {
    const [petData, setPetData] = useState(data);
    const [fieldError, setFieldError] = useState({});
    const [isBtnDisabled, setIsBtnDisabled] = useState(true);
    const [zipCodeData, setZipCodeData] = useState({});
    const [inProcess, setInProcess] = useState(false);

    const checkFormInvalid = () => {
        const copyFormData = { ...petData };
        delete copyFormData.marketing;

        return Object.values(copyFormData).some((v) => v === null || `${v}`.trim() === "" || v === false);
    };

    const validateGetQuoteForm = () => {
        const isFormDataInvalid = !checkFormInvalid();

        return isFormDataInvalid && zipCodeData?.isValid && zipCodeData?.isCovered;
    };

    const verifyZipCode = async (zip) => {
        try {
            setInProcess(true);

            var { payload } = await zipCodeValidationApi.invoke({
                zip,
                currentDateTime: moment().format(appConstant.ISO_DATE),
            });

            const data = get(payload, "payload.responses[0]", {});
            const isCovered = get(data, "isCovered", false);
            const isValid = get(data, "isValid", false);

            setZipCodeData(data);

            const copyFieldError = { ...fieldError };

            if (isCovered && isValid) {
                delete copyFieldError["zipCode"];
            } else {
                copyFieldError["zipCode"] = true;
            }

            setFieldError(copyFieldError);

            setInProcess(false);
        } catch (error) {
            setInProcess(false);

            const copyFieldError = { ...fieldError };

            copyFieldError["zipCode"] = true;

            setFieldError(copyFieldError);

            logger.error(`Error while verify zip code ${zip}:`, error);
        }
    };

    const handleOnChange = (e, value) => {
        setPetData({
            ...petData,
            [value.name]: value.value,
            ...(value.name === "zipCode" && {
                isValidZip: false,
                marketing: false,
            }),
        });

        if (value.name === "zipCode") {
            setZipCodeData({});
        }

        const copyFieldError = { ...fieldError };

        delete copyFieldError[value.name];

        setFieldError(copyFieldError);
    };

    const handleOnBlur = (e) => {
        const targetName = e.target.id;
        const value = e.target.value;

        const copyFieldError = { ...fieldError };

        if (value?.trim() === "") {
            copyFieldError[targetName] = true;
        } else {
            delete copyFieldError[targetName];
        }

        if (targetName === "zipCode" && !petData.isValidZip) {
            copyFieldError[targetName] = true;
        }

        setFieldError(copyFieldError);
    };

    const handleKeyupZipCode = (e) => {
        const value = e.target.value;

        const ZipCode = normalizeZipCode(value);

        const usPattern = new RegExp(appConstant.US_ZIP_CODE_PATTERN);
        const caPattern = new RegExp(appConstant.CAN_ZIP_CODE_PATTERN);

        const isValidZip = usPattern.test(ZipCode.toString()) || caPattern.test(ZipCode.toString().replace(/\W+/g, ""));

        setPetData({ ...petData, isValidZip });

        if (isValidZip) {
            verifyZipCode(ZipCode);
        }
    };

    useEffect(() => {
        if (!isEmpty(zipCodeData)) {
            const isUSZipCode =
                zipCodeData && petData.zipCode && zipCodeData?.countryCode === appConstant.COUNTRY_CODE_US;
            const isCAZipCode =
                zipCodeData && petData.zipCode && zipCodeData?.countryCode === appConstant.COUNTRY_CODE_CANADA;

            if (isUSZipCode) {
                setPetData({ ...petData, marketing: true });
            }

            if (isCAZipCode) {
                setPetData({ ...petData, marketing: false });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zipCodeData]);

    const handleSubmit = () => {
        handleFormChange(petData);
        setStepNo(7);
    };

    useEffect(() => {
        const getValidators = () => [validateGetQuoteForm];

        const validators = getValidators();

        const isDisabled = validators.some((validator) => validator.call());

        setIsBtnDisabled(!isDisabled);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petData]);

    const heading1 = `${formData.petAge} years, Cute! ${formData?.petName?.trim()}’s still a pup.`;
    const heading2 = `What’s <b>${formData?.petName?.trim()}’s zip or postcode</b>?`;

    return (
        <>
            <div className="conversationalForm__container__formContainer__petData">
                <div className="conversationalForm__container__formContainer__petData__heading">
                    <div
                        className="conversationalForm__container__formContainer__petData__heading__desktop"
                        dangerouslySetInnerHTML={{
                            __html: heading1,
                        }}
                    />
                    <div
                        className="conversationalForm__container__formContainer__petData__heading__desktop"
                        dangerouslySetInnerHTML={{
                            __html: heading2,
                        }}
                    />
                    <div
                        className="conversationalForm__container__formContainer__petData__heading__mobile"
                        dangerouslySetInnerHTML={{
                            __html: `${heading1} ${heading2}`,
                        }}
                    />
                </div>
                <div className="conversationalForm__container__formContainer__petData__inputDiv">
                    <div className="conversationalForm__container__formContainer__petData__inputDiv__fieldGroup">
                        <TextInput
                            name="zipCode"
                            value={petData.zipCode}
                            onChange={handleOnChange}
                            identifier="zipCode"
                            type="text"
                            inputVariant="filled"
                            inputLabel={"enter your zipCode"}
                            onBlur={handleOnBlur}
                            onKeyUp={handleKeyupZipCode}
                            error={
                                fieldError["zipCode"] && petData.zipCode.trim() === ""
                                    ? "Please enter your valid zip/postal code."
                                    : fieldError["zipCode"] && petData.zipCode.trim() !== ""
                                    ? "Oops! That's an invalid zip/postal code."
                                    : false
                            }
                        />
                    </div>
                </div>
                <div className="conversationalForm__container__formContainer__petData__button">
                    <Button
                        type="button"
                        size="large"
                        onClick={handleSubmit}
                        fullWidth
                        variant="contained"
                        disabled={isBtnDisabled}
                        id="submit"
                        className={inProcess ? `no-click submit-button` : "submit-button"}
                        style={isBtnDisabled ? { ...styles?.button?.disable } : { ...styles?.button?.active }}
                    >
                        {inProcess ? (
                            <span className="button-loading-content">
                                Verifying ZipCode
                                <ButtonLoading />
                            </span>
                        ) : (
                            <>Next</>
                        )}
                    </Button>
                </div>
            </div>
        </>
    );
};

FormStep6.propTypes = {
    styles: PropTypes.object,
    handleFormChange: PropTypes.func,
    data: PropTypes.object,
    setStepNo: PropTypes.func,
    formData: PropTypes.object,
};

export default FormStep6;
